import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {isiOS, useBrand} from '../../helpers';
import {Flex} from '../../styles';
import Loader from '../Loader';

import MutedIcon from '../../assets/muted-icon.svg';
import UnmutedIcon from '../../assets/unmuted-icon.svg';
import {getCurrentLang} from '../../utils/i18n';
import {useTranslation} from 'react-i18next';

const AdsPlayer = ({closed, hasFocus, append, eventHandlers}) => {
    const {t} = useTranslation();
    const {user, config} = useSelector(state => state);
    const tve = user && user.user_type === 'tve';

    const [mounted, setMounted] = useState(false);
    const [adsLoaded, setAdsLoaded] = useState(false);
    const [adsInitialized, setAdsInitialized] = useState(false);
    const [adsMuted, setAdsMuted] = useState(null);
    const [adsPaused, setAdsPaused] = useState(false);
    const [adsStarted, setAdsStarted] = useState(false);
    const [adsManager, setAdsManager] = useState(null);
    const [adsLoader, setAdsLoader] = useState(null);
    const [adDisplayContainer, setAdDisplayContainer] = useState(null);
    const [isFocused, setIsFocused] = useState(hasFocus || false);
    const [canAutoPlayMuted, setCanAutoPlayMuted] = useState(null);
    const [canAutoPlay, setCanAutoPlay] = useState(null);
    const [errorCounter, setErrorCounter] = useState(0);
    const brand = useBrand();

    let adContainer;

    useEffect(() => {
        checkPlayCapabilities();

        window.addEventListener('focus', () => {
            !isFocused && setIsFocused(true);
        });
        window.addEventListener('click', () => {
            !isFocused && setIsFocused(true);
        });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const imaSDKScript = document.createElement('script');

        mounted && handleAddIMAScriptTag(imaSDKScript);

        return () => {
            mounted && imaSDKScript && document.body.removeChild(imaSDKScript);
        };

        // eslint-disable-next-line
    }, [mounted]);

    useEffect(() => {
        if (mounted && adsLoader) {
            adsLoader.addEventListener(
                window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                onAdsManagerLoaded,
                false,
            );
            adsLoader.addEventListener(
                window.google.ima.AdErrorEvent.Type.AD_ERROR,
                onAdError,
                false,
            );
        }

        return () => {
            if (mounted && adsLoader) {
                adsLoader.removeEventListener(
                    window.google.ima.AdsManagerLoadedEvent.Type
                        .ADS_MANAGER_LOADED,
                    onAdsManagerLoaded,
                );
                adsLoader.removeEventListener(
                    window.google.ima.AdErrorEvent.Type.AD_ERROR,
                    onAdError,
                );
            }
        };

        // eslint-disable-next-line
    }, [mounted, isFocused, adsLoader]);

    useEffect(() => {
        adsManager && adDisplayContainer && loadAds();

        // eslint-disable-next-line
    }, [adsManager, adDisplayContainer]);

    useEffect(() => {
        return () => {
            if (adsManager) {
                adsManager.pause();
                adsManager.destroy();
            }
        };

        // eslint-disable-next-line
    }, [adsManager]);

    useEffect(() => {
        if (adsInitialized && adsManager && (canAutoPlay || canAutoPlayMuted)) {
            if (canAutoPlay || canAutoPlayMuted) handleStartAds();
        }

        // eslint-disable-next-line
    }, [adsManager, adsInitialized, canAutoPlay, canAutoPlayMuted]);

    useEffect(() => {
        if (closed && adsManager) {
            adsManager.setVolume(0);
            adsManager.pause();
            adsManager.destroy();
        }

        // eslint-disable-next-line
    }, [adsManager, closed]);

    const checkPlayCapabilities = async () => {
        const contentVideo = document.createElement('video');

        let canPlayUnmuted = true;

        contentVideo.play().catch(_ => {
            log('CANNOT PLAY UNMUTED');
            canPlayUnmuted = false;
        });

        setTimeout(() => {
            setCanAutoPlay(canPlayUnmuted);
            !canPlayUnmuted && log('CANNOT PLAY UNMUTED');
        }, 1000);

        contentVideo.setAttribute('playsinline', '');
        contentVideo.setAttribute('muted', '');
        contentVideo.volume = 0;
        contentVideo.muted = true;

        let canPlayMuted = true;

        contentVideo.play().catch(_ => {
            log('CANNOT PLAY MUTED');
            canPlayMuted = false;
        });

        setTimeout(() => {
            setCanAutoPlayMuted(canPlayMuted);
            !canPlayMuted && log('CANNOT PLAY MUTED');
        }, 1000);

        setMounted(true);
    };

    const handleAddIMAScriptTag = imaSDKScript => {
        imaSDKScript.src = '//imasdk.googleapis.com/js/sdkloader/ima3.js';

        document.body.appendChild(imaSDKScript);

        imaSDKScript.addEventListener('error', err => {
            log('IMA SDK ERROR LOADING SCRIPT: ', err);

            handleAdsComplete();
        });
        imaSDKScript.addEventListener('load', () => {
            initializeIMA();
            log('ima sdk loaded');
        });
    };

    const handleAdsComplete = () => {
        log('AD COMPLETE');

        if (adsManager) {
            adsManager.pause();
            adsManager.destroy();
        }

        if (eventHandlers && eventHandlers.onCompleted) {
            eventHandlers.onCompleted();
        }
    };

    const initializeIMA = () => {
        log('initializing IMA');

        adContainer = document.getElementById('ad-container');
        adContainer.addEventListener('click', adContainerClick);

        window.google.ima.settings.setLocale(getCurrentLang());

        let newAdDisplayContainer = new window.google.ima.AdDisplayContainer(
            adContainer,
            null,
        );

        setAdDisplayContainer(newAdDisplayContainer);

        let newAdsLoader = new window.google.ima.AdsLoader(
            newAdDisplayContainer,
        );

        setAdsLoader(newAdsLoader);

        var adsRequest = new window.google.ima.AdsRequest();

        let adTagUrl = undefined;

        if (config.player_settings && config.player_settings.ads) {
            adTagUrl = tve
                ? config.player_settings.ads.tveAd
                : config.player_settings.ads.telcoAd;

            if (config.btw && config.btw.enabled && !user.jwtToken) {
                adTagUrl =
                    config.player_settings.ads.tveAd ||
                    config.player_settings.ads.telcoAd;
            }

            if (brand === 'cnito') {
                adTagUrl = `https://pubads.g.doubleclick.net/gampad/ads?sz=970x250%7C320x50%7C300x250%7C728x90%7C640x480&iu=/99911224/ci_[countryname]la&env=vp&impl=s&gdfp_req=1&output=vast&unviewed_position_start=1&description_url=http%3A%2F%2Fwww.cartoonitola.com&correlator=`;
            } else if (brand === 'dkids') {
                adTagUrl = undefined;
            }
        }

        if (adTagUrl && adTagUrl.indexOf('[countryname]')) {
            if (user.countryIsoCode) {
                adTagUrl = adTagUrl.replace(
                    '[countryname]',
                    ['ar', 'br', 'co', 'cl', 'mx', 've'].includes(
                        user.countryIsoCode.toLowerCase(),
                    )
                        ? user.countryIsoCode.toLowerCase()
                        : 'la',
                );
            } else {
                adTagUrl = undefined;
            }
        }

        adsRequest.setAdWillAutoPlay(true);
        adsRequest.setAdWillPlayMuted(true);

        adsRequest.adTagUrl =
            adTagUrl ||
            // eslint-disable-next-line max-len
            `https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/single_ad_samples&ciu_szs=300x250&impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&cust_params=deployment%3Ddevsite%26sample_ct%3Dlinear&correlator=`;

        newAdsLoader.requestAds(adsRequest);
    };

    const adContainerClick = event => {
        log('ad container clicked');
    };

    const loadAds = () => {
        if (adsLoaded) {
            return;
        }

        setAdsLoaded(true);

        log('loading ads');

        adDisplayContainer.initialize();

        try {
            adsManager.init('100%', '100%', window.google.ima.ViewMode.NORMAL);
            setAdsInitialized(true);
        } catch (adError) {
            log('AdsManager could not be started');
            handleAdsComplete();
        }
    };

    const handleStartAds = () => {
        if (adsInitialized) {
            let unmuted =
                isFocused &&
                canAutoPlay &&
                document.hasFocus() &&
                document.visibilityState === 'visible'
                    ? true
                    : false;

            unmuted = isiOS() ? false : unmuted;

            setAdsMuted(!unmuted);

            adsManager.setVolume(unmuted ? 1 : 0);

            adsManager.start();
        }
    };

    const onAdsManagerLoaded = adsManagerLoadedEvent => {
        // eslint-disable-next-line max-len
        const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();

        adsRenderingSettings.uiElements = [
            window.google.ima.UiElements.COUNTDOWN,
            window.google.ima.UiElements.AD_ATTRIBUTION,
        ];

        let manager = adsManagerLoadedEvent.getAdsManager(
            null,
            adsRenderingSettings,
        );

        setAdsManager(manager);

        manager.addEventListener(
            window.google.ima.AdErrorEvent.Type.AD_ERROR,
            e => onAdError(e, manager),
        );
        manager.addEventListener(window.google.ima.AdEvent.Type.PAUSED, e => {
            setAdsPaused(true);
            log('AD PAUSED');
        });
        manager.addEventListener(window.google.ima.AdEvent.Type.RESUMED, () => {
            setAdsPaused(false);
            log('AD RESUMED');
        });
        manager.addEventListener(window.google.ima.AdEvent.Type.SKIPPED, () => {
            log('AD SKIPPED');

            handleAdsComplete();
        });
        manager.addEventListener(window.google.ima.AdEvent.Type.STARTED, () => {
            log('AD STARTED');
            setAdsStarted(true);

            if (eventHandlers && eventHandlers.onStarted) {
                eventHandlers.onStarted();
            }
        });
        manager.addEventListener(
            window.google.ima.AdEvent.Type.INTERACTION,
            () => log('AD INTERACTION'),
        );
        manager.addEventListener(
            window.google.ima.AdEvent.Type.IMPRESSION,
            () => log('AD IMPRESSION'),
        );
        manager.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, () =>
            handleAdsComplete(),
        );
        manager.addEventListener(window.google.ima.AdEvent.Type.CLICK, () => {
            setAdsPaused(true);
            log('AD CLICK');
        });
        manager.addEventListener(
            window.google.ima.AdEvent.Type.LOADED,
            onAdLoaded,
        );
    };

    const onAdLoaded = adEvent => {
        log('AD LOADED');
        var ad = adEvent.getAd();
        if (!ad.isLinear()) {
            handleAdsComplete();
        }
    };

    const onAdError = (adErrorEvent, manager) => {
        const error = adErrorEvent.getError().g;
        // eslint-disable-next-line
        console.error('ADS ERROR', error);

        // handleAdsComplete();
        adsManager ? adsManager.destroy() : manager && manager.destroy();

        setMounted(false);
        setAdsManager(null);
        setAdsLoaded(false);
        setAdsInitialized(false);
        setAdsLoader(null);

        if (errorCounter <= 1) {
            setErrorCounter(errorCounter + 1);
            setCanAutoPlay(errorCounter < 1 ? true : false);
            setCanAutoPlayMuted(errorCounter < 1 ? true : false);

            setTimeout(() => {
                setMounted(true);
            }, 2000);
        } else {
            handleAdsComplete();
        }
    };

    const log = (...args) => {
        let Logger = console;

        (localStorage.debugAds && localStorage.debugAds.toString()) ===
            'true' && Logger.log(...args);
    };

    return (
        <Container>
            <div id="ad-container">
                {!adsInitialized || !adsLoaded ? (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                ) : (
                    <>
                        {adsMuted !== null && adsStarted && (
                            <MuteButton
                                onClick={() => {
                                    adsManager &&
                                        adsManager.setVolume(!adsMuted ? 0 : 1);
                                    setAdsMuted(!adsMuted);
                                }}
                            >
                                <img
                                    alt="Mute/Unmute"
                                    src={adsMuted ? MutedIcon : UnmutedIcon}
                                />
                                {adsMuted ? t('ads.unmute') : t('ads.mute')}
                            </MuteButton>
                        )}

                        {(adsPaused ||
                            (adsStarted === false &&
                                canAutoPlay === false &&
                                canAutoPlayMuted === false)) &&
                            adsInitialized &&
                            adsLoaded && (
                                <ControlUI
                                    onClick={() => {
                                        setAdsMuted(false);
                                        adsManager.setVolume(1);
                                        !adsStarted && handleStartAds();
                                        adsPaused && adsManager.resume();
                                    }}
                                />
                            )}
                    </>
                )}
            </div>
            {append}
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    #ad-container {
        background-color: rgb(8, 0, 8);
        border-radius: 6px;
        overflow: hidden;
        position: relative;
        width: 85%;
        height: auto;
        max-width: 809px;
        max-height: 455px;
        min-height: ${window.innerHeight > 455 ? '455px' : 'calc(90% - 130px)'};

        cursor: pointer;

        & > div:first-child {
            position: initial !important;
            width: 100%;
            height: 100%;
            aspect-ratio: 16/9;
        }
    }
`;

/* eslint-disable max-len */
const MuteButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: rgba(0, 0, 0, 0.7);
    border: 0;
    border-radius: 6px;
    left: 24px;
    bottom: 24px;
    cursor: pointer;
    height: 40px;
    padding: 0 12px;
    position: absolute;
    min-width: 64px;
    width: auto;
    z-index: 999;

    color: #fff;
    font-size: 16px;
    font-family: 'TTNormsProExtraBlack';
    font-weight: bold;
    text-transform: uppercase;

    img {
        width: 28px;
        height: 28px;
        object-fit: contain;
        margin-right: 8px;
    }
`;

const LoaderContainer = styled(Flex)`
    position: absolute !important;
    top: 0px;
    left: 0px;
    bottom: 0px;
    right: 0px;

    height: 100% !important;
`;

const ControlUI = styled.div`
    position: absolute;
    background-color: rgba(0, 0, 0, 0.6);
    top: 0px;
    left: 0px;
    bottom: 0px;
    right: 0px;

    background-position: center;
    background-repeat: no-repeat;
    background-size: 7em;
    transition: opacity 1s linear;
    background-image: url('https://cn.i.cdn.ti-platform.com/static/cnplus/play_big.svg');
`;
/* eslint-enable max-len */

export default AdsPlayer;
